<script setup lang="ts">
import { Checkbox } from '@/components/ui/checkbox';

/**
 * The per-page "Enabled" checkbox. Granting flips the user's page grant,
 * which shows / hides the matching sidebar nav-link and unlocks the route
 * for the user. It does NOT change the user's model permissions.
 *
 * Always rendered (so the affordance is discoverable) but visually muted +
 * disabled until a real user is picked.
 */
defineProps<{
  pageId: number;
  granted: boolean;
  /** True when the parent's grant mutation is in-flight OR no user is picked. */
  disabled: boolean;
  /** Drives the muted styling + title hint when no user is selected. */
  selectedUserId: number | null;
}>();

const emit = defineEmits<{
  /** Bool only — Reka's Checkbox can emit 'indeterminate' for the tri-state
   *  flavour, which doesn't apply here. */
  change: [value: boolean];
}>();

function onUpdate(value: boolean | 'indeterminate') {
  if (typeof value === 'boolean') {
    emit('change', value);
  }
}
</script>

<template>
  <label
    class="inline-flex items-center gap-2 text-xs"
    :class="selectedUserId === null ? 'text-muted-foreground' : ''"
    :data-testid="`permissions-page-${pageId}-access-label`"
    :title="selectedUserId === null ? 'Select a user above to toggle access' : undefined"
  >
    <Checkbox
      :model-value="granted"
      :disabled="disabled"
      :data-testid="`permissions-page-${pageId}-access`"
      @update:model-value="onUpdate"
    />
    Enabled
  </label>
</template>
